import React from 'react';
import {
  DndContext,
  DragStartEvent,
  DragOverEvent,
  DragMoveEvent,
  DragEndEvent,
  Modifier,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { SortableContext, horizontalListSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import type { TabProps } from '@mui/material';
import {
  Box,
  Tabs,
  Tab,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  Close,
  PushPin,
  PushPinOutlined,
  Close as CloseIcon,
  ChevronLeft,
  ChevronRight,
  Close as CloseAllIcon,
} from '@mui/icons-material';
import { useTabManager } from '../../context/TabManagerContext';
import { CHROME_OVERLAY_Z, getNavTokens } from '../../navigation/navTokens';
import { useLocation } from 'react-router-dom';

/**
 * Aba arrastável. Mantém o papel de "tab" (o atributo role do dnd-kit ficaria por cima)
 * e só começa a arrastar depois de alguns pixels, então o clique continua selecionando.
 */
const SortableTab = React.forwardRef<HTMLDivElement, TabProps & { sortId: string }>(
  ({ sortId, style, ...props }, _ref) => {
    const { setNodeRef, transform, transition, isDragging, listeners, attributes } = useSortable({ id: sortId });
    const { role: _role, ...a11y } = attributes;
    return (
      <Tab
        ref={setNodeRef}
        {...a11y}
        {...listeners}
        {...props}
        data-dragging={isDragging ? 'true' : undefined}
        style={{
          ...style,
          transform: CSS.Translate.toString(transform),
          transition,
          zIndex: isDragging ? 3 : undefined,
          cursor: isDragging ? 'grabbing' : undefined,
        }}
      />
    );
  },
);
SortableTab.displayName = 'SortableTab';

interface TabManagerProps {
  onTabChange?: (tabId: string) => void;
  onDraggingFirstChange?: (dragging: boolean) => void;
  onDragCornerChange?: (radius: number | null) => void;
}

/**
 * Abas abertas, dentro da barra superior do Layout (que já fica acima do backdrop
 * dos modais keep-alive). A aba ativa tem o tom do conteúdo e encaixa nele.
 */
export const TabManager: React.FC<TabManagerProps> = ({ onTabChange, onDraggingFirstChange, onDragCornerChange }) => {
  const location = useLocation();
  const t = getNavTokens(useTheme());
  const {
    tabs,
    activeTabId,
    closeTab,
    closeOtherTabs,
    closeAllTabs,
    pinTab,
    moveTab,
    unpinTab,
    navigateToTab,
    refreshTabData,
  } = useTabManager();

  const [contextMenu, setContextMenu] = React.useState<{
    mouseX: number;
    mouseY: number;
    tabId: string;
  } | null>(null);
  const lastTabClickRef = React.useRef<{ tabId: string; time: number } | null>(null);
  const barRef = React.useRef<HTMLDivElement>(null);
  const dragStartRef = React.useRef<{ firstLeft: number; scrollLeft: number } | null>(null);
  const dragFrameRef = React.useRef<number | null>(null);
  const cornerRadiusRef = React.useRef<number | null>(null);
  const cornerCallbackRef = React.useRef(onDragCornerChange);
  cornerCallbackRef.current = onDragCornerChange;
  const [firstDisplaced, setFirstDisplaced] = React.useState(false);
  const [pointerFocus, setPointerFocus] = React.useState(false);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));
  const restrictToTabBar = React.useCallback<Modifier>(({ transform, activeNodeRect }) => {
    const start = dragStartRef.current;
    if (!start || !activeNodeRect) return { ...transform, y: 0 };
    const scroller = barRef.current?.querySelector<HTMLElement>('.MuiTabs-scroller');
    const firstLeft = start.firstLeft - ((scroller?.scrollLeft ?? start.scrollLeft) - start.scrollLeft);
    return { ...transform, x: Math.max(transform.x, firstLeft - activeNodeRect.left), y: 0 };
  }, []);
  const setFirstSeparated = (separated: boolean) => {
    setFirstDisplaced(separated);
    onDraggingFirstChange?.(separated);
  };
  const handleDragStart = ({ active }: DragStartEvent) => {
    const first = barRef.current?.querySelector<HTMLElement>('.MuiTab-root');
    const scroller = barRef.current?.querySelector<HTMLElement>('.MuiTabs-scroller');
    dragStartRef.current = first ? { firstLeft: first.getBoundingClientRect().left, scrollLeft: scroller?.scrollLeft ?? 0 } : null;
    setFirstSeparated(active.id === tabs[0]?.id);
    const measureSelected = () => {
      const start = dragStartRef.current;
      const selected = barRef.current?.querySelector<HTMLElement>('.MuiTab-root.Mui-selected');
      const scroll = barRef.current?.querySelector<HTMLElement>('.MuiTabs-scroller');
      if (!start) return;
      const firstLeft = start.firstLeft - ((scroll?.scrollLeft ?? start.scrollLeft) - start.scrollLeft);
      const radius = selected ? Math.min(10, Math.max(0, selected.getBoundingClientRect().left - firstLeft)) : 10;
      if (cornerRadiusRef.current !== radius) {
        cornerRadiusRef.current = radius;
        cornerCallbackRef.current?.(radius);
      }
      dragFrameRef.current = requestAnimationFrame(measureSelected);
    };
    dragFrameRef.current = requestAnimationFrame(measureSelected);
  };
  const updateFirstPosition = ({ active, over, delta }: DragMoveEvent | DragOverEvent) => {
    const start = dragStartRef.current;
    const activeLeft = active.rect.current.initial?.left;
    const scroller = barRef.current?.querySelector<HTMLElement>('.MuiTabs-scroller');
    const firstLeft = start ? start.firstLeft - ((scroller?.scrollLeft ?? start.scrollLeft) - start.scrollLeft) : null;
    const activeSelected = tabs.find((tab) => tab.path === location.pathname)?.id === active.id ||
      (!tabs.some((tab) => tab.path === location.pathname) && activeTabId === active.id);
    // A curva de encaixe ocupa 10px: a folha deve preencher o canto ao longo
    // dessa aproximação, antes de a aba alcançar exatamente a primeira posição.
    const connectedToFirst = activeSelected && firstLeft !== null && activeLeft != null && activeLeft + delta.x <= firstLeft + 10;
    setFirstSeparated(!connectedToFirst && (active.id === tabs[0]?.id || over?.id === tabs[0]?.id));
  };
  const clearDrag = () => {
    dragStartRef.current = null;
    if (dragFrameRef.current !== null) cancelAnimationFrame(dragFrameRef.current);
    dragFrameRef.current = null;
    cornerRadiusRef.current = null;
    onDragCornerChange?.(null);
    setFirstSeparated(false);
  };
  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    clearDrag();
    if (over && active.id !== over.id) moveTab(String(active.id), String(over.id));
  };
  // Abas que não cabem: setas discretas (só do lado que tem mais abas), degradê na borda
  // e a roda do mouse rolando a barra na horizontal.
  const [edges, setEdges] = React.useState({ left: false, right: false });
  const tabCount = tabs?.length ?? 0;

  React.useEffect(() => () => {
    if (dragFrameRef.current !== null) cancelAnimationFrame(dragFrameRef.current);
  }, []);

  React.useEffect(() => {
    const scroller = barRef.current?.querySelector<HTMLElement>('.MuiTabs-scroller');
    if (!scroller) return undefined;
    const update = () => {
      const left = scroller.scrollLeft > 1;
      const right = scroller.scrollLeft + scroller.clientWidth < scroller.scrollWidth - 1;
      setEdges((prev) => (prev.left === left && prev.right === right ? prev : { left, right }));
    };
    const onWheel = (event: WheelEvent) => {
      if (scroller.scrollWidth <= scroller.clientWidth) return;
      if (Math.abs(event.deltaY) <= Math.abs(event.deltaX)) return;
      scroller.scrollLeft += event.deltaY;
      event.preventDefault();
    };
    update();
    scroller.addEventListener('scroll', update, { passive: true });
    scroller.addEventListener('wheel', onWheel, { passive: false });
    const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(update) : null;
    observer?.observe(scroller);
    return () => {
      scroller.removeEventListener('scroll', update);
      scroller.removeEventListener('wheel', onWheel);
      observer?.disconnect();
    };
  }, [tabCount]);

  const fadeMask = edges.left || edges.right
    ? `linear-gradient(to right, ${edges.left ? 'transparent 0, #000 24px' : '#000 0'}, ${edges.right ? '#000 calc(100% - 40px), transparent 100%' : '#000 100%'})`
    : 'none';

  if (!tabs || tabs.length === 0) {
    return null;
  }

  const handleTabChange = (event: React.SyntheticEvent, newValue: string) => {
    navigateToTab(newValue);
    onTabChange?.(newValue);
  };

  const handleCloseTab = (event: React.MouseEvent, tabId: string) => {
    event.stopPropagation();
    closeTab(tabId);
  };

  const handleRefreshTab = (event: React.MouseEvent, tabId: string) => {
    event.stopPropagation();
    navigateToTab(tabId);
    refreshTabData(tabId);
  };

  const handleTabClick = (event: React.MouseEvent, tabId: string) => {
    const now = Date.now();
    const lastClick = lastTabClickRef.current;

    lastTabClickRef.current = { tabId, time: now };

    if (lastClick?.tabId === tabId && now - lastClick.time <= 450) {
      handleRefreshTab(event, tabId);
      lastTabClickRef.current = null;
    }
  };

  const handleContextMenu = (event: React.MouseEvent, tabId: string) => {
    event.preventDefault();
    setContextMenu({
      mouseX: event.clientX + 2,
      mouseY: event.clientY - 6,
      tabId,
    });
  };

  const handleCloseContextMenu = () => {
    setContextMenu(null);
  };

  const handlePinTab = (tabId: string) => {
    const tab = tabs.find(t => t.id === tabId);
    if (tab) {
      if (tab.isPinned) {
        unpinTab(tabId);
      } else {
        pinTab(tabId);
      }
    }
    handleCloseContextMenu();
  };

  const handleCloseOtherTabs = (tabId: string) => {
    closeOtherTabs(tabId);
    handleCloseContextMenu();
  };

  const handleCloseAllTabs = () => {
    closeAllTabs();
    handleCloseContextMenu();
  };

  const routeTabId = tabs.find((tab) => tab.path === location.pathname)?.id;
  const selectedTabId = routeTabId || activeTabId || false;
  const contextTab = tabs.find(tab => tab.id === contextMenu?.tabId);
  const contextIndex = tabs.findIndex(tab => tab.id === contextMenu?.tabId);
  // Reordenar pelo teclado: o menu de contexto abre com a tecla de menu ou Shift+F10.
  const handleMoveTab = (offset: -1 | 1) => {
    const neighbor = tabs[contextIndex + offset];
    if (contextTab && neighbor) moveTab(contextTab.id, neighbor.id);
    handleCloseContextMenu();
  };

  return (
    <Box ref={barRef} data-mestti-tab-bar onPointerDownCapture={() => setPointerFocus(true)} onKeyDownCapture={() => setPointerFocus(false)} sx={{ flex: 1, minWidth: 0, alignSelf: 'stretch', display: 'flex', alignItems: 'flex-end' }}>
        <DndContext sensors={sensors} collisionDetection={closestCenter} modifiers={[restrictToTabBar]} onDragStart={handleDragStart} onDragMove={updateFirstPosition} onDragOver={updateFirstPosition} onDragEnd={handleDragEnd} onDragCancel={clearDrag}>
        <SortableContext items={tabs.map((tab) => tab.id)} strategy={horizontalListSortingStrategy}>
        <Tabs
          value={selectedTabId}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          slotProps={{
            scrollButtons: {
              sx: {
                width: 24,
                flex: 'none',
                alignSelf: 'center',
                height: 28,
                borderRadius: '14px',
                color: t.muted,
                '& svg': { fontSize: 18 },
                '&:hover': { color: t.ink, backgroundColor: t.hover },
                '&.Mui-disabled': { display: 'none' },
              },
            },
          }}
          aria-label="Abas abertas"
          data-first-displaced={firstDisplaced ? 'true' : undefined}
          data-pointer-focus={pointerFocus ? 'true' : undefined}
          sx={{
            minHeight: 0,
            width: '100%',
            '& .MuiTabs-indicator': { display: 'none' },
            '&[data-first-displaced="true"] .MuiTab-root.Mui-selected:first-of-type::before': { display: 'block' },
            '&[data-pointer-focus="true"] .MuiTab-root.Mui-focusVisible': { outline: 'none' },
            '& .MuiTabs-scroller': {
              maskImage: fadeMask,
              WebkitMaskImage: fadeMask,
              scrollbarWidth: 'none',
              '&::-webkit-scrollbar': { display: 'none' },
            },
            '& .MuiTabs-flexContainer': {
              alignItems: 'flex-end',
              gap: '2px',
              // Primeira aba encostada na borda do conteúdo; folga à direita para a curva.
              pr: '10px',
            },
            '& .MuiTab-root': {
              minHeight: 38,
              height: 38,
              overflow: 'visible',
              flex: '0 0 auto',
              minWidth: 0,
              maxWidth: 220,
              py: 0,
              pl: 1.5,
              // Espaço visual igual dos dois lados (o X tem folga interna de ~3,5 px).
              pr: 1,
              textTransform: 'none',
              fontSize: '0.84rem',
              fontWeight: 500,
              letterSpacing: 0,
              color: t.muted,
              borderRadius: '10px 10px 0 0',
              // Hover só no texto: fundo no vizinho brigava com a curva da aba ativa.
              '&:hover': { color: t.ink },
              // Aba ativa com o tom do conteúdo; a base curva para fora e encaixa na folha.
              '&.Mui-selected': {
                color: t.ink,
                backgroundColor: t.canvas,
                zIndex: 1,
                '&::before, &::after': {
                  content: '""',
                  position: 'absolute',
                  bottom: 0,
                  width: 10,
                  height: 10,
                  pointerEvents: 'none',
                },
                '&::before': {
                  left: -10,
                  background: `radial-gradient(circle at 0 0, transparent 10px, ${t.canvas} 10.5px)`,
                },
                '&::after': {
                  right: -10,
                  background: `radial-gradient(circle at 100% 0, transparent 10px, ${t.canvas} 10.5px)`,
                },
                // Primeira aba ativa: lado esquerdo reto, na mesma linha da borda do conteúdo.
                '&:first-of-type::before': { display: 'none' },
              },
              // Durante o arraste, qualquer aba precisa de uma superfície própria.
              '&[data-dragging="true"]': {
                color: t.ink,
                backgroundColor: t.chrome,
              },
              '&.Mui-selected[data-dragging="true"]': {
                backgroundColor: t.canvas,
              },
              '&.Mui-focusVisible': { outline: `2px solid ${t.focus}`, outlineOffset: -2 },
            },
          }}
        >
          {tabs.map((tab) => (
            <SortableTab
              key={tab.id}
              sortId={tab.id}
              disableRipple
              title={tab.label}
              value={tab.id}
              onClick={(event) => handleTabClick(event, tab.id)}
              onContextMenu={(event) => handleContextMenu(event, tab.id)}
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0, overflow: 'hidden' }}>
                  <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {tab.label}
                  </Box>

                  {(tab.isPinned || tab.isClosable) && (
                  <Box
                    className="tab-trailing"
                    sx={{
                      position: 'relative',
                      width: 22,
                      height: 22,
                      flexShrink: 0,
                    }}
                  >
                    {tab.isPinned && (
                      <Box
                        className="tab-pin"
                        aria-label="Fixada"
                        sx={{
                          position: 'absolute',
                          inset: 0,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          pointerEvents: 'none',
                          color: t.muted,
                        }}
                      >
                        <PushPin sx={{ fontSize: 14 }} />
                      </Box>
                    )}
                    {tab.isClosable && (
                      <IconButton
                        className="tab-close"
                        component="div"
                        disableRipple
                        size="small"
                        aria-label={`Fechar aba ${tab.label}`}
                        onClick={(e) => handleCloseTab(e, tab.id)}
                        onDoubleClick={(e) => e.stopPropagation()}
                        sx={{
                          position: 'absolute',
                          inset: 0,
                          p: 0,
                          color: t.muted,
                          opacity: 0,
                          pointerEvents: 'none',
                          '&:hover': { backgroundColor: t.hover, color: t.ink },
                        }}
                      >
                        <Close sx={{ fontSize: 15 }} />
                      </IconButton>
                    )}
                  </Box>
                  )}
                </Box>
              }
              sx={{
                '&.Mui-selected': {
                  // Sem fixar: X visível na aba ativa
                  '&:not(:has(.tab-pin)) .tab-close': { opacity: 1, pointerEvents: 'auto' },
                },
                // Fixada: alfinete visível; no hover vira X
                '&:hover, &:focus-within': {
                  '& .tab-pin': { opacity: 0 },
                  '& .tab-close': { opacity: 1, pointerEvents: 'auto' },
                },
                '&:not(:has(.tab-close)):hover .tab-pin': { opacity: 1 },
              }}
            />
          ))}
        </Tabs>
        </SortableContext>
        </DndContext>


        <Menu
          open={contextMenu !== null}
          onClose={handleCloseContextMenu}
          anchorReference="anchorPosition"
          anchorPosition={
            contextMenu !== null
              ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
              : undefined
          }
          disableScrollLock
          sx={{ zIndex: CHROME_OVERLAY_Z }}
          slotProps={{
            paper: {
              sx: {
                minWidth: 168,
                borderRadius: '12px',
                backgroundImage: 'none',
                boxShadow: '0 12px 32px rgba(15,23,42,0.18), 0 2px 6px rgba(15,23,42,0.08)',
              },
            },
            list: {
              dense: true,
              sx: { p: 0.5 },
            },
          }}
        >
          <MenuItem
            dense
            onClick={() => handlePinTab(contextMenu?.tabId || '')}
            sx={{ borderRadius: '8px', gap: 1, minHeight: 32 }}
          >
            <ListItemIcon sx={{ minWidth: 28 }}>
              {contextTab?.isPinned ? (
                <PushPinOutlined sx={{ fontSize: 16 }} />
              ) : (
                <PushPin sx={{ fontSize: 16 }} />
              )}
            </ListItemIcon>
            <ListItemText
              primary={contextTab?.isPinned ? 'Desafixar' : 'Fixar'}
              primaryTypographyProps={{ fontSize: '0.8rem' }}
            />
          </MenuItem>

          <MenuItem
            dense
            disabled={contextIndex <= 0}
            onClick={() => handleMoveTab(-1)}
            sx={{ borderRadius: '8px', gap: 1, minHeight: 32 }}
          >
            <ListItemIcon sx={{ minWidth: 28 }}>
              <ChevronLeft sx={{ fontSize: 16 }} />
            </ListItemIcon>
            <ListItemText primary="Mover para a esquerda" primaryTypographyProps={{ fontSize: '0.8rem' }} />
          </MenuItem>
          <MenuItem
            dense
            disabled={contextIndex < 0 || contextIndex >= tabs.length - 1}
            onClick={() => handleMoveTab(1)}
            sx={{ borderRadius: '8px', gap: 1, minHeight: 32 }}
          >
            <ListItemIcon sx={{ minWidth: 28 }}>
              <ChevronRight sx={{ fontSize: 16 }} />
            </ListItemIcon>
            <ListItemText primary="Mover para a direita" primaryTypographyProps={{ fontSize: '0.8rem' }} />
          </MenuItem>

          <Divider sx={{ my: 0.25 }} />

          <MenuItem
            dense
            onClick={() => handleCloseOtherTabs(contextMenu?.tabId || '')}
            sx={{ borderRadius: '8px', gap: 1, minHeight: 32 }}
          >
            <ListItemIcon sx={{ minWidth: 28 }}>
              <CloseIcon sx={{ fontSize: 16 }} />
            </ListItemIcon>
            <ListItemText
              primary="Fechar outras"
              primaryTypographyProps={{ fontSize: '0.8rem' }}
            />
          </MenuItem>

          <MenuItem
            dense
            onClick={handleCloseAllTabs}
            sx={{ borderRadius: '8px', gap: 1, minHeight: 32 }}
          >
            <ListItemIcon sx={{ minWidth: 28 }}>
              <CloseAllIcon sx={{ fontSize: 16 }} />
            </ListItemIcon>
            <ListItemText
              primary="Fechar todas"
              primaryTypographyProps={{ fontSize: '0.8rem' }}
            />
          </MenuItem>
        </Menu>
    </Box>
  );
};
